import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";

import { NAICS_OPTIONS, NAICS_OTHER } from "./naics-options";

const TERMS_URL = "https://cinch.io/terms/";

const ROUNDED_INPUT_SX = {
  "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
  "& .MuiInputLabel-asterisk": { display: "none" },
};

const REQUIRED_ADORNMENT = (
  <InputAdornment position="end">
    <Box
      component="span"
      sx={{ color: "#D32F2F", fontSize: "1rem", lineHeight: 1 }}
    >
      *
    </Box>
  </InputAdornment>
);

const SUBMIT_BUTTON_SX = {
  py: 1.25,
  borderRadius: 1.5,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "1rem",
  color: "#fff",
  bgcolor: "#0875BF",
  boxShadow: "none",
  "&:hover": { bgcolor: "#076AA8", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#0875BF", opacity: 0.6, color: "#fff" },
};

/**
 * Signup form card used in both the hero and the pricing section of the
 * combined AI Voice landing page. All form state is lifted to the parent so
 * both instances read/write the same values — a visitor who starts filling
 * the hero form sees their input mirrored in the pricing form (and vice
 * versa), and PostHog/captcha/submission flow stays a single pipeline no
 * matter which Submit button they press.
 */
export default function VoiceAgentSignupForm({
  form,
  fieldErrors,
  error,
  signupEnabled,
  signupDisabledMessage,
  submitting,
  onChange,
  onSubmit,
  onSignInClick,
  heading = "Start Your Free Trial",
  subheading = "Try 25 minutes free. No credit card required.",
  submitLabel = "Start Free Trial",
  elevation = 3,
}) {
  const naicsIsOther = form.naics_choice === NAICS_OTHER;

  return (
    <Paper
      elevation={elevation}
      sx={{
        width: "100%",
        maxWidth: 558,
        boxSizing: "border-box",
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        boxShadow: "0 1px 7px rgba(0,0,0,0.12)",
      }}
    >
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            color: "#111827",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            lineHeight: 1.3,
          }}
        >
          {heading}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: "rgba(0,0,0,0.6)",
            fontSize: "1rem",
            lineHeight: 1.5,
          }}
        >
          {subheading}
        </Typography>
      </Stack>

      {signupEnabled === false && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {signupDisabledMessage ||
            "Signups are temporarily unavailable. Please try again later or contact support."}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={onSubmit}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              label="First Name"
              required
              fullWidth
              value={form.first_name}
              onChange={onChange("first_name")}
              error={Boolean(fieldErrors.first_name)}
              helperText={fieldErrors.first_name?.[0]}
              autoComplete="given-name"
              inputProps={{ maxLength: 60 }}
              InputProps={{ endAdornment: REQUIRED_ADORNMENT }}
              sx={ROUNDED_INPUT_SX}
            />
            <TextField
              label="Last Name"
              required
              fullWidth
              value={form.last_name}
              onChange={onChange("last_name")}
              error={Boolean(fieldErrors.last_name)}
              helperText={fieldErrors.last_name?.[0]}
              autoComplete="family-name"
              inputProps={{ maxLength: 60 }}
              InputProps={{ endAdornment: REQUIRED_ADORNMENT }}
              sx={ROUNDED_INPUT_SX}
            />
          </Stack>

          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            value={form.email}
            onChange={onChange("email")}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email?.[0]}
            autoComplete="email"
            inputProps={{
              inputMode: "email",
              autoCapitalize: "none",
              autoCorrect: "off",
              spellCheck: false,
              maxLength: 254,
            }}
            InputProps={{ endAdornment: REQUIRED_ADORNMENT }}
            sx={ROUNDED_INPUT_SX}
          />

          <TextField
            label="Company Name"
            required
            fullWidth
            value={form.company_name}
            onChange={onChange("company_name")}
            error={Boolean(fieldErrors.company_name)}
            helperText={fieldErrors.company_name?.[0]}
            autoComplete="organization"
            inputProps={{ maxLength: 150 }}
            InputProps={{ endAdornment: REQUIRED_ADORNMENT }}
            sx={ROUNDED_INPUT_SX}
          />

          <TextField
            select
            label="Industry"
            required
            fullWidth
            value={form.naics_choice}
            onChange={onChange("naics_choice")}
            error={Boolean(fieldErrors.naics)}
            helperText={fieldErrors.naics?.[0]}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: 2.5 }}>
                  <Box
                    component="span"
                    sx={{ color: "#D32F2F", fontSize: "1rem", lineHeight: 1 }}
                  >
                    *
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={ROUNDED_INPUT_SX}
          >
            {NAICS_OPTIONS.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.title}
              </MenuItem>
            ))}
            <MenuItem value={NAICS_OTHER}>Not listed</MenuItem>
          </TextField>

          {naicsIsOther && (
            <TextField
              label="Tell us about your industry"
              required
              fullWidth
              value={form.naics_other}
              onChange={onChange("naics_other")}
              placeholder="e.g. Mobile dog grooming"
              inputProps={{ maxLength: 255 }}
              InputProps={{ endAdornment: REQUIRED_ADORNMENT }}
              sx={ROUNDED_INPUT_SX}
            />
          )}

          <FormControl
            required
            error={Boolean(fieldErrors.terms_accepted)}
            component="fieldset"
            sx={{ mt: 0.5 }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.terms_accepted}
                  onChange={onChange("terms_accepted")}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)" }}>
                  I agree to the{" "}
                  <Link
                    href={TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    Terms of Service
                  </Link>
                  .
                </Typography>
              }
              sx={{ mr: 0 }}
            />
            {fieldErrors.terms_accepted && (
              <FormHelperText sx={{ ml: 4 }}>
                {fieldErrors.terms_accepted[0]}
              </FormHelperText>
            )}
          </FormControl>

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={submitting || signupEnabled === false}
            sx={SUBMIT_BUTTON_SX}
          >
            {submitLabel}
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            Already have an account?{" "}
            <Link
              href="https://marketing.cinch.io/sign-in"
              underline="hover"
              onClick={onSignInClick}
            >
              Sign in
            </Link>
          </Typography>
        </Stack>
      </form>
    </Paper>
  );
}
